import React from "react";

/**
 * Bandeau de logos partenaires qui défile de gauche à droite en boucle
 * continue, sans vide. Le défilement se met en pause au survol /
 * focus et s'arrête si l'utilisateur a demandé à réduire les animations
 * (voir .partners-marquee dans App.css).
 */
const PartnerItem = ({ partner }) => {
  const content = partner.logo ? (
    <img
      src={partner.logo}
      alt={partner.name}
      // Pas de lazy loading : les logos cachés hors du bandeau ne seraient
      // jamais chargés avant d'entrer à l'écran.
      loading="eager"
      // mix-blend-multiply : le fond blanc des logos JPG se fond dans le crème.
      className="h-16 w-auto max-w-[180px] object-contain mix-blend-multiply hover:scale-105 transition-transform"
    />
  ) : (
    <span className="flex flex-col items-center justify-center h-16 px-5 rounded-xl border border-[#DCB071]/40 bg-[#F5F3EB] text-center">
      <span className="text-sm font-semibold text-[#21167D] whitespace-nowrap">{partner.name}</span>
      {partner.location && (
        <span className="text-xs text-[#4A4458]/80">{partner.location}</span>
      )}
    </span>
  );

  if (!partner.url) return content;

  return (
    <a
      href={partner.url}
      target="_blank"
      rel="noopener noreferrer"
      title={partner.name}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#21167D] rounded-xl"
    >
      {content}
    </a>
  );
};

// Nombre minimal de logos par copie, pour qu'une copie soit plus large que
// l'écran même quand une catégorie n'a que 2 ou 3 partenaires.
const MIN_ITEMS_PER_COPY = 12;

const PartnersMarquee = ({ title, partners }) => {
  if (!partners || partners.length === 0) return null;

  // Une "copie" = la liste répétée jusqu'à MIN_ITEMS_PER_COPY logos. La piste
  // contient deux copies identiques et glisse d'exactement une copie : la
  // seconde prend la place de la première, d'où une boucle sans vide.
  const repeat = Math.ceil(MIN_ITEMS_PER_COPY / partners.length);
  const copy = Array.from({ length: repeat }, () => partners).flat();

  // Durée d'un passage complet : ~2,5 s par logo (augmenter pour ralentir).
  const duration = `${Math.max(15, Math.round(copy.length * 2.5))}s`;

  // Seule la première occurrence de chaque partenaire est exposée aux lecteurs
  // d'écran et au clavier ; les répétitions sont décoratives.
  const items = [...copy, ...copy].map((partner, index) => {
    const isDuplicate = index >= partners.length;
    return (
      <li
        key={`${partner.id}-${index}`}
        className={`flex-shrink-0 px-4 flex items-center${isDuplicate ? " partners-marquee__duplicate" : ""}`}
        aria-hidden={isDuplicate || undefined}
        inert={isDuplicate}
      >
        <PartnerItem partner={partner} />
      </li>
    );
  });

  return (
    <section className="py-14 bg-[#FDFBF5] border-t border-[#DCB071]/30" aria-label={title}>
      <h2 className="font-display text-3xl font-semibold text-[#21167D] text-center mb-10 px-4">
        {title}
      </h2>
      <div className="partners-marquee" style={{ "--marquee-duration": duration }}>
        <ul className="partners-marquee__track">{items}</ul>
      </div>
    </section>
  );
};

export default PartnersMarquee;
